import { bem, Button, Icon, Tag } from '@distribusion/owl-frontend-ui'
import cn from 'classnames'
import React, { PropsWithChildren, useMemo } from 'react'

import ancillaryUtils from '@lib/ancillary'
import connectionUtils from '@lib/connection'
import { useTranslation } from '@lib/i18n'
import { useEnabledContext } from '@pages/Checkout/SeatSelection/NewSeats/context/enabled'
import { useSeatAncillaryContext } from '@pages/Checkout/SeatSelection/NewSeats/context/seatAncillary'
import { useSeatSelectionContext } from '@pages/Checkout/SeatSelection/NewSeats/context/seatSelection'
import SeatsPreview from '@pages/Checkout/SeatSelection/NewSeats/SeatsPreview'
import { Warning } from '@pages/Checkout/SeatSelection/NewSeats/Warning'
import { useSettings } from '@queries/settings'

import '@pages/Checkout/SeatSelection/NewSeats/SegmentSeats.scss'
interface SegmentSeatsProps {
  connection: Connection
  segment: Segment
  direction: Seat.TripDirection
  connectionFareClass: string
}

const ActionButton: React.FC<PropsWithChildren<{ onClick: () => void; dataTag?: string }>> = ({
  children,
  ...props
}) => (
  <Button className="cell no-grow basis-auto" variant="text" fullWidth={false} {...props}>
    {children}
  </Button>
)

export const SegmentSeats: React.FC<SegmentSeatsProps> = ({ connection, segment, direction, connectionFareClass }) => {
  const { t } = useTranslation()
  const [{ seatSelection }] = useSettings()
  const { enabled: externallyEnabled } = useEnabledContext()
  const { openModal, scheme } = useSeatSelectionContext()
  const { ancillaries, globalAncillaries, isAncillaryActive } = useSeatAncillaryContext()
  const relevantAncillaries = useMemo(
    () =>
      ancillaries.filter(
        ancillary => globalAncillaries.includes(ancillary) || ancillary.segments?.some(({ id }) => id === segment.id),
      ),
    [ancillaries, globalAncillaries, segment.id],
  )
  const seatScheme = useMemo(
    () => scheme[direction]?.find(schemeSegment => connectionUtils.isSameSegment(schemeSegment, segment)),
    [direction, scheme, segment],
  )

  const activeFromAncillary = !relevantAncillaries.length || relevantAncillaries.some(isAncillaryActive)
  const enabled = externallyEnabled && activeFromAncillary && seatScheme
  const { seats: allSeats } = useSeatSelectionContext()
  const seats = allSeats?.[direction]?.[segment.index]
  const mandatory = seatSelection.mandatory || relevantAncillaries.some(ancillaryUtils.isMandatory)

  return (
    <div className={cn(bem('new-seats', 'segment'), 'column')}>
      <div className={cn(bem('new-seats', 'segment-description'), 'row', 'body-16')}>
        <div>
          <span className="bolder">{(segment.index ?? connection.segments.indexOf(segment)) + 1}</span>/
          {connection.segments.length}
        </div>
        <div className={cn(bem('new-seats', 'segment-stations'), 'row', 'bolder', 'items-center')}>
          {segment.departureStation.name}
          <Icon name="arrow-right" size="small" />
          {segment.arrivalStation.name}
        </div>
      </div>
      <div className={cn(bem('new-seats', 'segment-info-and-actions'), 'row', 'column-sm', 'items-center', 'body-14')}>
        <div className={cn(bem('new-seats', 'segment-info-section'), 'row', 'cell', 'grow', 'items-center')}>
          {mandatory ? (
            <Tag variant="primary">{t('checkout.seats.mandatory')}</Tag>
          ) : (
            <Tag variant="warning">{t('checkout.seats.optional')}</Tag>
          )}
          {!seatScheme && !seats?.length && <Warning>{t('checkout.seats.alerts.selection_not_available')}</Warning>}
          {enabled && seatScheme && !seats?.length && (
            <div className={cn(bem('new-seats', 'segment-info'), 'row', 'items-center')}>
              <Icon size="small" name="info" />
              {t('checkout.seats.alerts.automatic_selection')}
            </div>
          )}
          {!!seats?.length && (
            <SeatsPreview
              className="no-grow"
              direction={direction}
              connectionFareClass={connectionFareClass}
              scheme={seatScheme!}
              segment={segment}
              seats={seats}
            />
          )}
        </div>
        {enabled && (
          <>
            {!!seats?.length && (
              <ActionButton onClick={() => openModal(direction, segment)}>
                <Icon size="medium" name="edit" />
                {t('checkout.seats.actions.change')}
              </ActionButton>
            )}
            {!seats?.length && (
              <ActionButton
                onClick={() => openModal(direction, segment)}
                dataTag="checkout-seat-selection-select-seats"
              >
                <Icon size="medium" name="seat" />
                {t('checkout.seats.actions.select')}
              </ActionButton>
            )}
          </>
        )}
      </div>
    </div>
  )
}
